<template>
  <BaseSheet :isOpen="isOpen" size="lg" :title="t('newcomponent.headline')" @close="handleClose(false)">
    <template #header-extra>
      <div class="px-5 xl:px-6 pb-4 xl:pt-5">
        <BottomSheetStepper :steps="stepLabels" :currentStep="currentStep" />
      </div>
    </template>

    <!-- Step Content -->
    <div class="flex-1 overflow-hidden px-5 min-h-0">
      <div class="relative h-full">
        <Transition :name="transitionName">
          <div :key="currentStepId" class="absolute inset-0 overflow-y-auto">
            <h2 class="text-[16px] font-bold text-foreground tracking-tight mb-3">{{ stepLabels[currentStep] }}</h2>
            <ComponentTypeForm v-if="currentStepId === 'type'" />
            <ComponentDateForm v-else-if="currentStepId === 'dates'" />
            <ComponentIntervalForm v-else-if="currentStepId === 'interval'" />
            <ComponentDatasetForm v-else-if="currentStepId === 'data'" />
            <ComponentChartForm v-else-if="currentStepId === 'title'" />
            <div class="h-4"></div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex-shrink-0 px-5 py-4 flex items-center gap-3 cs-footer">
      <button v-if="currentStep > 0"
        @click="goBack"
        class="flex-1 h-[48px] rounded-[14px] text-[14px] font-semibold cs-btn-cancel active:scale-[0.97] transition-all duration-150">
        {{ t('Previous') }}
      </button>
      <button
        @click="goForward"
        :disabled="sending"
        class="flex-1 h-[48px] rounded-[14px] text-[14px] font-bold text-white cs-btn-cta flex items-center justify-center gap-2 active:scale-[0.97] transition-all duration-150 disabled:opacity-70 disabled:active:scale-100">
        <svg v-if="sending" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
          fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span v-if="!sending">{{ isLastStep ? t('newcomponent.bottomsheet.create') : t('Next') }}</span>
      </button>
    </div>
  </BaseSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseSheet from '../dialogs/BaseSheet.vue'
import { useNewComponentStore } from '../../stores/newComponentStore'
import { useSalesReportsStore } from '../../stores/salesReports'
import BottomSheetStepper from './BottomSheetStepper.vue'
import ComponentTypeForm from '../form/ComponentTypeForm.vue'
import ComponentDateForm from '../form/ComponentDateForm.vue'
import ComponentIntervalForm from '../form/ComponentIntervalForm.vue'
import ComponentDatasetForm from '../form/ComponentDatasetForm.vue'
import ComponentChartForm from '../form/ComponentChartForm.vue'

const { t } = useI18n()
const newComponentStore = useNewComponentStore()
const salesReportsStore = useSalesReportsStore()

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
}>()

const currentStep = ref(0)
const sending = ref(false)
const transitionName = ref('tab-slide-left')

type StepId = 'type' | 'dates' | 'interval' | 'data' | 'title'

const isCustom = computed(() => newComponentStore.newComponent.type === 'custom')

const steps = computed<StepId[]>(() => {
  if (isCustom.value) {
    return ['type', 'dates', 'interval', 'data', 'title']
  }
  return ['type', 'dates', 'data', 'title']
})

const stepLabels = computed(() => {
  if (isCustom.value) {
    return [
      t('newcomponent.steps.period'),
      t('newcomponent.steps.dates'),
      t('newcomponent.steps.interval'),
      t('newcomponent.steps.data_choice'),
      t('newcomponent.steps.charts')
    ]
  }
  return [
    t('newcomponent.steps.period'),
    t('newcomponent.steps.dates'),
    t('newcomponent.steps.data_choice'),
    t('newcomponent.steps.charts')
  ]
})

const currentStepId = computed(() => steps.value[currentStep.value])
const isLastStep = computed(() => currentStep.value === steps.value.length - 1)

const goBack = () => {
  if (currentStep.value > 0) {
    transitionName.value = 'tab-slide-right'
    currentStep.value--
  }
}

const goForward = async () => {
  if (isLastStep.value) {
    sending.value = true
    const success = await newComponentStore.send()
    sending.value = false

    if (success) {
      handleClose(false)
      await salesReportsStore.initCenterComponents()
      await salesReportsStore.fetchAllSalesReports(true)
    }
  } else {
    transitionName.value = 'tab-slide-left'
    currentStep.value++
  }
}

const handleClose = (value: boolean) => {
  if (!value) {
    currentStep.value = 0
    newComponentStore.reset()
  }
  emit('update:isOpen', value)
}

// Reset when opened
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    currentStep.value = 0
    newComponentStore.reset()
  }
})

// When switching from custom to non-custom, make sure step doesn't overflow
watch(isCustom, () => {
  if (currentStep.value >= steps.value.length) {
    currentStep.value = steps.value.length - 1
  }
})
</script>

<style scoped>
/* ── Light mode ── */
.cs-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.cs-btn-cancel {
  color: var(--muted-foreground, #64748b);
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.cs-btn-cancel:active {
  background: rgba(0, 0, 0, 0.07);
}
.cs-btn-cta {
  background: linear-gradient(135deg, #06C8FF 0%, #009FE3 100%);
  box-shadow: 0 6px 24px rgba(6, 200, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

/* ── Dark mode ── */
:is(.dark *).cs-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
:is(.dark *).cs-btn-cancel {
  color: var(--muted-foreground, #94a3b8);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.07);
}
:is(.dark *).cs-btn-cancel:active {
  background: rgba(255, 255, 255, 0.10);
}
:is(.dark *).cs-btn-cta {
  box-shadow: 0 4px 20px rgba(6, 200, 255, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.10);
}

/* ── Step transitions ── */
.tab-slide-left-enter-active,
.tab-slide-right-enter-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.tab-slide-left-leave-active,
.tab-slide-right-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.tab-slide-left-enter-from {
  opacity: 0;
  transform: translateX(40%);
}
.tab-slide-left-leave-to {
  opacity: 0;
  transform: translateX(-40%);
}
.tab-slide-right-enter-from {
  opacity: 0;
  transform: translateX(-40%);
}
.tab-slide-right-leave-to {
  opacity: 0;
  transform: translateX(40%);
}
</style>
